import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Switch,
  InputNumber,
  Row,
  Col,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import lightappService from '../../services/lightappService';

const { Option } = Select;

const Products = () => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // "Create from template" modal state
  const [tplModalVisible, setTplModalVisible] = useState(false);
  const [tplSaving, setTplSaving] = useState(false);
  const [tplEnvKeys, setTplEnvKeys] = useState([]);
  const [tplForm] = Form.useForm();

  useEffect(() => {
    fetchProducts();
    fetchTemplates();
    fetchEndpoints();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await lightappService.getProducts({ page_size: 200 });
      if (response.success) {
        // index() returns { list, total, ... }
        const data = response.data;
        setProducts(data?.list || data || []);
      } else {
        message.error(response.error || 'Failed to fetch products');
      }
    } catch (error) {
      message.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await lightappService.getTemplates();
      if (response.success) {
        setTemplates(response.data?.list || response.data || []);
      }
    } catch (error) {
      // non-fatal
    }
  };

  const fetchEndpoints = async () => {
    try {
      const response = await lightappService.getEndpoints();
      if (response.success) {
        setEndpoints(response.data?.list || response.data || []);
      }
    } catch (error) {
      // non-fatal
    }
  };

  const handleAdd = () => {
    setEditingProduct(null);
    form.resetFields();
    form.setFieldsValue({
      node_type: 'docker',
      billing_mode: 'fixed',
      is_active: true,
      memory_reservation: 256,
      memory_limit: 512,
      cpu_limit: 1.0,
      volume_size: 10,
      traffic_limit: 0,
      price: 0,
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingProduct(record);
    form.setFieldsValue({
      name: record.name,
      display_name: record.display_name,
      template_id: record.template_id,
      default_endpoint_id: record.default_endpoint_id,
      node_type: record.node_type,
      price: record.price,
      billing_mode: record.billing_mode,
      memory_reservation: record.memory_reservation,
      memory_limit: record.memory_limit,
      cpu_limit: record.cpu_limit,
      volume_size: record.volume_size,
      traffic_limit: record.traffic_limit,
      is_active: !!record.is_active,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await lightappService.deleteProduct(id);
      if (response.success) {
        message.success('Product deleted');
        fetchProducts();
      } else {
        message.error(response.error || 'Failed to delete product');
      }
    } catch (error) {
      message.error('Failed to delete product');
    }
  };

  const handleToggleActive = async (record) => {
    try {
      const response = await lightappService.toggleProductActive(record.id);
      if (response.success) {
        message.success('Status updated');
        fetchProducts();
      } else {
        message.error(response.error || 'Failed to update status');
      }
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const response = editingProduct
        ? await lightappService.updateProduct(editingProduct.id, values)
        : await lightappService.saveProduct(values);

      if (response.success) {
        message.success(editingProduct ? 'Product updated' : 'Product created');
        setModalVisible(false);
        fetchProducts();
      } else {
        message.error(response.error || 'Failed to save product');
      }
    } catch (error) {
      if (error?.errorFields) return; // form validation error
      message.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFromTemplate = () => {
    tplForm.resetFields();
    setTplEnvKeys([]);
    tplForm.setFieldsValue({
      billing_mode: 'fixed',
      price: 0,
      memory_reservation: 256,
      memory_limit: 512,
      cpu_limit: 1.0,
      volume_size: 10,
      traffic_limit: 0,
      user_configurable_env: [],
    });
    setTplModalVisible(true);
  };

  // When a template is picked, pull its details to prefill name/display_name
  // and expose its env_schema keys as selectable "user configurable" vars.
  const handleTemplateSelect = async (templateId) => {
    const local = templates.find((t) => t.id === templateId);
    if (local) {
      tplForm.setFieldsValue({
        name: local.name ? `${local.name}-product` : undefined,
        display_name: local.display_name || local.name,
      });
    }
    try {
      const response = await lightappService.getTemplate(templateId);
      if (response.success) {
        const detail = response.data || {};
        const schema = detail.env_schema || {};
        setTplEnvKeys(Object.keys(schema));
        if (detail.display_name || detail.name) {
          tplForm.setFieldsValue({
            display_name: tplForm.getFieldValue('display_name') || detail.display_name || detail.name,
          });
        }
      } else {
        setTplEnvKeys([]);
      }
    } catch (error) {
      setTplEnvKeys([]);
    }
  };

  const handleCreateFromTemplate = async () => {
    try {
      const values = await tplForm.validateFields();
      setTplSaving(true);
      const response = await lightappService.createProductFromTemplate(values);
      if (response.success) {
        message.success('已从模板创建产品');
        setTplModalVisible(false);
        fetchProducts();
      } else {
        message.error(response.error || '从模板创建产品失败');
      }
    } catch (error) {
      if (error?.errorFields) return; // form validation error
      message.error('从模板创建产品失败');
    } finally {
      setTplSaving(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
    },
    {
      title: 'Name',
      dataIndex: 'display_name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>{text || record.name}</span>
          <span style={{ color: '#999', fontSize: 12 }}>{record.name}</span>
        </Space>
      ),
    },
    {
      title: 'Template',
      dataIndex: ['template', 'name'],
      render: (text, record) => record.template?.name || '-',
    },
    {
      title: 'Node Type',
      dataIndex: 'node_type',
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      render: (price, record) => (
        <span>
          {Number(price) > 0 ? `$${price}` : 'Free'}
          {record.billing_mode ? ` / ${record.billing_mode}` : ''}
        </span>
      ),
    },
    {
      title: 'Resources',
      render: (_, record) => (
        <span style={{ fontSize: 12, color: '#666' }}>
          {record.memory_limit}MB / {record.cpu_limit} vCPU / {record.volume_size}GB
        </span>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      render: (active, record) => (
        <Switch checked={!!active} onChange={() => handleToggleActive(record)} size="small" />
      ),
    },
    {
      title: 'Actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this product?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <AppstoreOutlined /> 轻应用套餐管理
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ThunderboltOutlined />} onClick={handleOpenFromTemplate}>
            从模板创建产品
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            New Product
          </Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={products}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No products yet. Click “New Product” to create one."
            />
          ),
        }}
      />

      <Modal
        title={editingProduct ? 'Edit Product' : 'New Product'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Internal Name"
                rules={[{ required: true, message: 'Name is required' }]}
              >
                <Input placeholder="nginx-web" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="display_name"
                label="Display Name"
                rules={[{ required: true, message: 'Display name is required' }]}
              >
                <Input placeholder="Nginx Web Server" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="template_id" label="Template">
                <Select allowClear placeholder="Select a template" showSearch optionFilterProp="children">
                  {templates.map((t) => (
                    <Option key={t.id} value={t.id}>
                      {t.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="default_endpoint_id" label="Default Endpoint">
                <Select allowClear placeholder="Auto-select" showSearch optionFilterProp="children">
                  {endpoints.map((e) => (
                    <Option key={e.id} value={e.id}>
                      {e.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="node_type" label="Node Type">
                <Select>
                  <Option value="docker">docker</Option>
                  <Option value="lxd">lxd</Option>
                  <Option value="swarm">swarm</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="price" label="Price">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="billing_mode" label="Billing Mode">
                <Select>
                  <Option value="fixed">fixed</Option>
                  <Option value="usage">usage</Option>
                  <Option value="free">free</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="memory_reservation" label="Mem Reservation (MB)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="memory_limit" label="Mem Limit (MB)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="cpu_limit" label="CPU Limit (vCPU)">
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="volume_size" label="Volume (GB)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="traffic_limit" label="Traffic Limit (bytes, 0 = unlimited)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="从模板创建产品"
        open={tplModalVisible}
        onCancel={() => setTplModalVisible(false)}
        onOk={handleCreateFromTemplate}
        confirmLoading={tplSaving}
        width={720}
        destroyOnClose
      >
        <Form form={tplForm} layout="vertical">
          <Form.Item
            name="template_id"
            label="模板"
            rules={[{ required: true, message: '请选择模板' }]}
            extra="将根据模板的环境变量结构自动生成产品的 env_config"
          >
            <Select
              placeholder="选择一个模板"
              showSearch
              optionFilterProp="children"
              onChange={handleTemplateSelect}
            >
              {templates.map((t) => (
                <Option key={t.id} value={t.id}>
                  {t.display_name || t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="内部名称"
                rules={[{ required: true, message: '请填写内部名称' }]}
              >
                <Input placeholder="nginx-web-product" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="display_name"
                label="显示名称"
                rules={[{ required: true, message: '请填写显示名称' }]}
              >
                <Input placeholder="Nginx Web Server" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="user_configurable_env"
            label="用户可配置环境变量"
            extra="勾选的变量将允许下单用户自行填写；未勾选的使用模板默认值"
          >
            <Select
              mode="multiple"
              allowClear
              placeholder={tplEnvKeys.length ? '选择用户可配置的变量' : '该模板未定义环境变量'}
              disabled={!tplEnvKeys.length}
            >
              {tplEnvKeys.map((k) => (
                <Option key={k} value={k}>
                  {k}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="default_endpoint_id" label="默认节点">
                <Select allowClear placeholder="自动选择" showSearch optionFilterProp="children">
                  {endpoints.map((e) => (
                    <Option key={e.id} value={e.id}>
                      {e.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="price" label="价格">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="billing_mode" label="计费模式">
                <Select>
                  <Option value="fixed">fixed</Option>
                  <Option value="usage">usage</Option>
                  <Option value="free">free</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="memory_reservation" label="内存预留 (MB)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="memory_limit" label="内存上限 (MB)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="cpu_limit" label="CPU 上限 (vCPU)">
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="volume_size" label="磁盘 (GB)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="traffic_limit" label="流量上限 (字节, 0 = 不限制)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Products;
